import { QuoteOrderModelType } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormElement, FormRender } from '../../../../../../../forms/FormRenderer/types'
import { parseAddressToOneLine } from '../../../../../../../utils/globals/address'
import { displayDate } from '../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { SellTagsPayload } from '../../../../../../../utils/reducers/sells/sellTags'
import { ITHeaderOrderTags } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { OrderHeaderURLs } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { getActiveCurrenciesDictionary, getUserPermissions } from '../../../../../../../utils/reducers/getters'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { permissionCheck } from '../../../../../../../baseComponents/PermissionChecker/function'
import { CustomSellSelectProject, CustomSellSelectCampaign } from '../../../../../../../specificComponents/sells/components'
import { InputWithLabelMargin } from '../../../../../../../forms/Input'

type QuoteReadOnlyHeaderMainProps = InputTableLeftTabsProps & {
    OrderHeaderURLs: OrderHeaderURLs
    sellTags: SellTagsPayload
}

const QuoteReadOnlyHeaderMain: React.FC<QuoteReadOnlyHeaderMainProps> = ({ handleChange, headerData, sellTags, setCompleteData }) => {
    const { t } = useTwinTranslation()
    const { memorizedAddresses, activeCurrencies, customerProjects, customerCampaigns, getCustomerCampaigns, getCustomerProjects } = useQuoteReadOnlyHeaderMainLogic({ headerData })
    let extraFields: FormElement<keyof QuoteOrderModelType>[] = []
    if (headerData.CustomerAddressId ) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} className={returnClassOfSpanTemplate(5)} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            }
        })
    }
    const sections: FormRender<QuoteOrderModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} showCustomerProjectName={false} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData.custom_name} className={returnClassOfSpanTemplate(5)}/>
                    }
                },
                {
                    name: 'CustomerProjectId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectProject getCompleteData={getCustomerProjects} handleChange={handleChange} headerData={headerData} items={extraProps.customerProjects} classNameField={returnClassOfSpanTemplate(5)} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerProjects: customerProjects || {}
                    }
                },
                {
                    name: 'CampaignCustomerId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectCampaign getCompleteData={getCustomerCampaigns} handleChange={handleChange} headerData={headerData} items={extraProps.customerCampaigns} label={t('campaign', 'Campaña')} classNameField={returnClassOfSpanTemplate(5) + '-mt-15 -mb-8'} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerCampaigns: customerCampaigns || {}
                    }
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateQuoteOrder', 'Fecha del presupuesto')} value={displayDate(new Date(value))} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('quoteN', 'Nº presupuesto')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('workingCenter', 'Centro de trabajo')} value={headerData.WorkingCenter.name || ''} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} className={returnClassOfSpanTemplate(2)} />
                    }
                },
                ...extraFields,
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={memorizedAddresses.workingCenterAddress} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddresses.workingCenterAddress
                    }
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderOrderTags selectedTags={extraProps.selectedTags} dictTags={sellTags || []} headerData={headerData} onChange={handleChange} className={returnClassOfSpanTemplate(5)} setCompleteData={setCompleteData} />)
                    },
                    extraProps: { selectedTags: headerData?.tags }
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
    )
}

interface QuoteReadOnlyHeaderMainLogicProps {
    headerData: TwinDictionary
}

export const useQuoteReadOnlyHeaderMainLogic = ({ headerData }: QuoteReadOnlyHeaderMainLogicProps) => {
    const [customerProjects, setCustomerProjects] = useState<TwinDictionary[]>([{ [headerData?.CustomerProject?.id]: headerData?.CustomerProject }])
    const [customerCampaigns, setCustomerCampaigns] = useState<TwinDictionary[]>([{ [headerData?.CampaignCustomer?.id]: headerData?.CampaignCustomer?.Campaign }])
    const CustomerAddress = headerData.CustomerAddress
    const WorkingCenterAddress = headerData.WorkingCenterAddress
    const CustomerId = headerData.CustomerId
    const activeCurrencies = getActiveCurrenciesDictionary()
    const memorizedAddresses = useMemo(() => {
        return {
            workingCenterAddress: parseAddressToOneLine(WorkingCenterAddress),
            customerAddress: parseAddressToOneLine(CustomerAddress)
        }
    }, [WorkingCenterAddress, CustomerAddress])

    const getCustomerProjects = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', {
            where:
            {
                'CustomerProject.active': true,
                CustomerId
            },
        })
        if (result) {
            const projects: TwinDictionary[] = []
            for (const element of result) {
                projects.push({ ...element, value: element.id, text: element?.name })
            }
            setCustomerProjects(projects)
        }
    }, [CustomerId, setCustomerProjects])

    const getCustomerCampaigns = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/campaign/customer/getAllCampaignCustomers', {
            where:
            {
                CustomerId
            },
        })
        if (result) {
            const campaigns: TwinDictionary[] = []
            for (const element of result) {
                campaigns.push({ ...element?.Campaign, id: element?.id, value: element.id, text: element?.Campaign?.name})
            }
            setCustomerCampaigns(campaigns)
        }
    }, [CustomerId, setCustomerCampaigns])

    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.projects.main'), 'read') || permissionCheck(getUserPermissions('customer.profile.projects'), 'read')) {
            getCustomerProjects()
        }
    }, [getCustomerProjects])

    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.campaigns.main'), 'read') || permissionCheck(getUserPermissions('customer.profile.campaign'), 'read') || permissionCheck(getUserPermissions('customer.campaigns.inner'), 'read')) {
            getCustomerCampaigns()
        }
    }, [getCustomerCampaigns])

    return { memorizedAddresses, activeCurrencies, customerProjects, customerCampaigns, getCustomerCampaigns, getCustomerProjects }
}

export default QuoteReadOnlyHeaderMain